import { useState, useEffect, useRef } from "react";
import { Plus, Edit2, Trash2, Home, X, Save } from "lucide-react";
import toast from "react-hot-toast";
import { roomService } from "../../services/roomService";
import { uploadImages } from "../../services/uploadService";
import { imgUrl } from "../../utils/imgUrl";

const CATEGORIES = [
  { value: "luxury", label: "Luxury" },
  { value: "deluxe", label: "Deluxe" },
];

const emptyForm = {
  name: "",
  room_number: "",
  capacity: 2,
  price_per_night: "",
  description: "",
  status: "available",
  category: "luxury",
  amenities: "",
  existingImages: [],
  newFiles: [],
};

const ManageRooms = () => {
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editRoom, setEditRoom] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const imgFileRef = useRef(null);

  const loadRooms = () => {
    setLoading(true);
    roomService
      .getAllRooms()
      .then((res) => setRooms(res.data.rooms || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadRooms();
  }, []);

  const openAdd = () => {
    setEditRoom(null);
    setForm(emptyForm);
    setError("");
    setShowModal(true);
  };
  const openEdit = (room) => {
    setEditRoom(room);
    setForm({
      name: room.name,
      room_number: room.room_number || "",
      capacity: room.capacity,
      price_per_night: room.price_per_night,
      description: room.description || "",
      status: room.status,
      amenities: (room.amenities || []).join(", "),
      category: room.category || "standard",
      existingImages: room.images || [],
      newFiles: [],
    });
    setError("");
    setShowModal(true);
  };

  const removeExisting = (idx) =>
    setForm((prev) => ({
      ...prev,
      existingImages: prev.existingImages.filter((_, i) => i !== idx),
    }));

  const handleSave = async () => {
    if (!form.name || !form.room_number || !form.price_per_night) {
      setError("Name, room number and price are required.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const newUrls =
        form.newFiles.length > 0 ? await uploadImages(form.newFiles) : [];
      const payload = {
        name: form.name,
        room_number: form.room_number,
        capacity: Number(form.capacity),
        price_per_night: Number(form.price_per_night),
        description: form.description,
        status: form.status,
        amenities: form.amenities
          ? form.amenities
              .split(",")
              .map((a) => a.trim())
              .filter(Boolean)
          : [],
        images: [...form.existingImages, ...newUrls],
        category: form.category || "standard",
      };
      if (editRoom) {
        await roomService.updateRoom(editRoom.id, payload);
        toast.success("Room updated successfully!");
      } else {
        await roomService.createRoom(payload);
        toast.success("Room created successfully!");
      }
      setShowModal(false);
      loadRooms();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to save room.");
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async (id) => {
    try {
      await roomService.deleteRoom(id);
      setRooms((prev) => prev.filter((r) => r.id !== id));
      toast.success("Room deleted.");
    } catch (err) {
      toast.error(err.response?.data?.message || "Cannot delete room.");
    }
  };

  const handleDelete = (id) => {
    toast(
      (t) => (
        <div>
          <p style={{ margin: "0 0 10px", fontWeight: "500" }}>
            Delete this room? This cannot be undone.
          </p>
          <div style={{ display: "flex", gap: "8px" }}>
            <button
              onClick={() => {
                toast.dismiss(t.id);
                doDelete(id);
              }}
              style={{
                padding: "6px 14px",
                backgroundColor: "#dc2626",
                color: "white",
                border: "none",
                borderRadius: "6px",
                cursor: "pointer",
                fontSize: "13px",
                fontWeight: "500",
              }}
            >
              Delete
            </button>
            <button
              onClick={() => toast.dismiss(t.id)}
              style={{
                padding: "6px 14px",
                backgroundColor: "#e5e7eb",
                color: "#374151",
                border: "none",
                borderRadius: "6px",
                cursor: "pointer",
                fontSize: "13px",
                fontWeight: "500",
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      ),
      { duration: Infinity },
    );
  };

  const statusStyle = {
    available: { backgroundColor: "#d1fae5", color: "#065f46" },
    occupied: { backgroundColor: "#fee2e2", color: "#991b1b" },
    maintenance: { backgroundColor: "#fef3c7", color: "#92400e" },
  };

  return (
    <div style={s.container}>
      <div style={s.topBar}>
        <div>
          <h1 style={s.pageTitle}>Manage Rooms</h1>
          <p style={s.pageSubtitle}>
            {rooms.length} room{rooms.length !== 1 ? "s" : ""} total
          </p>
        </div>
        <button style={s.addBtn} onClick={openAdd}>
          <Plus size={18} /> Add Room
        </button>
      </div>

      {loading ? (
        <p style={{ color: "#6b7280" }}>Loading rooms...</p>
      ) : (
        <div style={s.grid}>
          {rooms.map((room) => (
            <div key={room.id} style={s.card}>
              <img
                src={
                  imgUrl(room.images?.[0]) ||
                  "https://images.unsplash.com/photo-1566665797739-1674de7a421a?w=400&h=300&fit=crop"
                }
                alt={room.name}
                style={s.roomImg}
                onError={(e) => {
                  e.target.src =
                    "https://images.unsplash.com/photo-1566665797739-1674de7a421a?w=400&h=300&fit=crop";
                }}
              />
              <div style={s.cardBody}>
                <div style={s.cardTop}>
                  <div>
                    <h3 style={s.roomName}>{room.name}</h3>
                    <p style={s.roomNum}>Room #{room.room_number}</p>
                  </div>
                  <span
                    style={{ ...s.badge, ...(statusStyle[room.status] || {}) }}
                  >
                    {room.status}
                  </span>
                </div>
                <div style={s.cardMeta}>
                  <span>Capacity: {room.capacity} guests</span>
                  <span style={s.price}>
                    ₹{Number(room.price_per_night).toLocaleString()}/night
                  </span>
                </div>
                {room.category && (
                  <span style={s.categoryTag}>
                    {CATEGORIES.find(c => c.value === room.category)?.label || room.category}
                  </span>
                )}
                {room.amenities?.length > 0 && (
                  <div style={s.amenitiesRow}>
                    {room.amenities.slice(0, 4).map((a, i) => (
                      <span key={i} style={s.amenityTag}>
                        {a}
                      </span>
                    ))}
                    {room.amenities.length > 4 && (
                      <span style={s.amenityTag}>
                        +{room.amenities.length - 4}
                      </span>
                    )}
                  </div>
                )}
                {room.description && <p style={s.desc}>{room.description}</p>}
                <div style={s.actions}>
                  <button style={s.editBtn} onClick={() => openEdit(room)}>
                    <Edit2 size={15} /> Edit
                  </button>
                  <button
                    style={s.deleteBtn}
                    onClick={() => handleDelete(room.id)}
                  >
                    <Trash2 size={15} /> Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {rooms.length === 0 && !loading && (
        <div style={s.empty}>
          <Home size={64} color="#d1d5db" />
          <p style={s.emptyText}>No rooms yet. Add your first room!</p>
        </div>
      )}

      {showModal && (
        <div style={s.overlay}>
          <div style={s.modal}>
            <div style={s.modalHeader}>
              <h2 style={s.modalTitle}>
                {editRoom ? "Edit Room" : "Add New Room"}
              </h2>
              <button style={s.closeBtn} onClick={() => setShowModal(false)}>
                <X size={20} />
              </button>
            </div>
            {error && <div style={s.errorBox}>{error}</div>}
            <div style={s.formGrid}>
              {[
                {
                  label: "Room Name",
                  key: "name",
                  type: "text",
                  placeholder: "e.g. Deluxe Cottage",
                },
                {
                  label: "Room Number",
                  key: "room_number",
                  type: "text",
                  placeholder: "e.g. 101",
                },
                {
                  label: "Capacity (guests)",
                  key: "capacity",
                  type: "number",
                  placeholder: "2",
                },
                {
                  label: "Price Per Night (₹)",
                  key: "price_per_night",
                  type: "number",
                  placeholder: "3500",
                },
              ].map((field) => (
                <div key={field.key} style={s.inputGroup}>
                  <label style={s.label}>{field.label}</label>
                  <input
                    type={field.type}
                    style={s.input}
                    value={form[field.key]}
                    placeholder={field.placeholder}
                    onChange={(e) =>
                      setForm({ ...form, [field.key]: e.target.value })
                    }
                  />
                </div>
              ))}
            </div>
            <div style={s.inputGroup}>
              <label style={s.label}>Category</label>
              <select
                style={s.input}
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
              >
                {CATEGORIES.map(c => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </select>
            </div>
            <div style={s.inputGroup}>
              <label style={s.label}>Status</label>
              <select
                style={s.input}
                value={form.status}
                onChange={(e) => setForm({ ...form, status: e.target.value })}
              >
                <option value="available">Available</option>
                <option value="occupied">Occupied</option>
                <option value="maintenance">Maintenance</option>
              </select>
            </div>
            <div style={s.inputGroup}>
              <label style={s.label}>
                Amenities <span style={s.hint}>(comma-separated)</span>
              </label>
              <input
                style={s.input}
                value={form.amenities}
                placeholder="WiFi, TV, Geyser, Mountain View"
                onChange={(e) =>
                  setForm({ ...form, amenities: e.target.value })
                }
              />
            </div>

            {/* Images */}
            <div style={s.inputGroup}>
              <label style={s.label}>Images</label>
              {form.existingImages.length > 0 && (
                <div
                  style={{
                    display: "flex",
                    gap: "8px",
                    flexWrap: "wrap",
                    marginBottom: "8px",
                  }}
                >
                  {form.existingImages.map((url, i) => (
                    <div key={i} style={{ position: "relative" }}>
                      <img
                        src={imgUrl(url)}
                        alt=""
                        style={{
                          width: "80px",
                          height: "60px",
                          objectFit: "cover",
                          borderRadius: "6px",
                        }}
                        onError={(e) => {
                          e.target.style.display = "none";
                        }}
                      />
                      <button
                        onClick={() => removeExisting(i)}
                        style={{
                          position: "absolute",
                          top: "-6px",
                          right: "-6px",
                          width: "18px",
                          height: "18px",
                          borderRadius: "50%",
                          backgroundColor: "#dc2626",
                          color: "white",
                          border: "none",
                          cursor: "pointer",
                          fontSize: "12px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}
              {form.newFiles.length > 0 && (
                <div
                  style={{
                    display: "flex",
                    gap: "8px",
                    flexWrap: "wrap",
                    marginBottom: "8px",
                  }}
                >
                  {form.newFiles.map((f, i) => (
                    <img
                      key={i}
                      src={URL.createObjectURL(f)}
                      alt=""
                      style={{
                        width: "80px",
                        height: "60px",
                        objectFit: "cover",
                        borderRadius: "6px",
                        border: "2px dashed #86efac",
                      }}
                    />
                  ))}
                </div>
              )}
              <input
                type="file"
                accept="image/*"
                multiple
                style={{ display: "none" }}
                ref={imgFileRef}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    newFiles: Array.from(e.target.files),
                  }))
                }
              />
              <button
                style={s.filePickerBtn}
                type="button"
                onClick={() => imgFileRef.current?.click()}
              >
                {form.newFiles.length > 0
                  ? `${form.newFiles.length} file(s) selected — click to change`
                  : "+ Add Images"}
              </button>
            </div>

            <div style={s.inputGroup}>
              <label style={s.label}>Description (Optional)</label>
              <textarea
                style={s.textarea}
                rows={3}
                value={form.description}
                onChange={(e) =>
                  setForm({ ...form, description: e.target.value })
                }
                placeholder="Room description..."
              />
            </div>
            <div style={s.modalFooter}>
              <button style={s.cancelBtn} onClick={() => setShowModal(false)}>
                Cancel
              </button>
              <button
                style={{ ...s.saveBtn, opacity: saving ? 0.7 : 1 }}
                onClick={handleSave}
                disabled={saving}
              >
                <Save size={16} /> {saving ? "Saving..." : "Save Room"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const s = {
  container: {
    padding: "32px",
    backgroundColor: "#f9fafb",
    minHeight: "100vh",
  },
  topBar: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: "28px",
  },
  pageTitle: {
    fontSize: "32px",
    fontWeight: "bold",
    color: "#111827",
    marginBottom: "6px",
  },
  pageSubtitle: { fontSize: "16px", color: "#6b7280" },
  addBtn: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    padding: "12px 20px",
    backgroundColor: "#16a34a",
    color: "white",
    border: "none",
    borderRadius: "10px",
    fontSize: "15px",
    fontWeight: "600",
    cursor: "pointer",
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
    gap: "20px",
  },
  card: {
    backgroundColor: "white",
    borderRadius: "12px",
    overflow: "hidden",
    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
  },
  roomImg: { width: "100%", height: "180px", objectFit: "cover" },
  cardBody: { padding: "20px" },
  cardTop: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: "12px",
  },
  roomName: {
    fontSize: "18px",
    fontWeight: "600",
    color: "#111827",
    marginBottom: "4px",
  },
  roomNum: { fontSize: "13px", color: "#6b7280" },
  badge: {
    padding: "4px 12px",
    borderRadius: "12px",
    fontSize: "12px",
    fontWeight: "600",
    textTransform: "capitalize",
    flexShrink: 0,
  },
  cardMeta: {
    display: "flex",
    justifyContent: "space-between",
    fontSize: "14px",
    color: "#6b7280",
    marginBottom: "10px",
  },
  price: { fontWeight: "600", color: "#16a34a" },
  amenitiesRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: "4px",
    marginBottom: "10px",
  },
  amenityTag: {
    padding: "2px 8px",
    backgroundColor: "#f0fdf4",
    color: "#15803d",
    borderRadius: "10px",
    fontSize: "11px",
    fontWeight: "500",
  },
  categoryTag: {
    display: "inline-block",
    padding: "2px 10px",
    backgroundColor: "#eff6ff",
    color: "#1d4ed8",
    borderRadius: "10px",
    fontSize: "11px",
    fontWeight: "600",
    marginBottom: "8px",
    textTransform: "capitalize",
  },
  desc: {
    fontSize: "13px",
    color: "#9ca3af",
    marginBottom: "16px",
    lineHeight: "1.5",
  },
  actions: {
    display: "flex",
    gap: "8px",
    paddingTop: "12px",
    borderTop: "1px solid #f3f4f6",
  },
  editBtn: {
    flex: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "6px",
    padding: "8px",
    backgroundColor: "#dbeafe",
    color: "#1d4ed8",
    border: "none",
    borderRadius: "8px",
    fontSize: "14px",
    fontWeight: "500",
    cursor: "pointer",
  },
  deleteBtn: {
    flex: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "6px",
    padding: "8px",
    backgroundColor: "#fee2e2",
    color: "#991b1b",
    border: "none",
    borderRadius: "8px",
    fontSize: "14px",
    fontWeight: "500",
    cursor: "pointer",
  },
  empty: {
    textAlign: "center",
    padding: "60px 20px",
    backgroundColor: "white",
    borderRadius: "12px",
  },
  emptyText: { fontSize: "18px", color: "#9ca3af", marginTop: "16px" },
  overlay: {
    position: "fixed",
    inset: 0,
    backgroundColor: "rgba(0,0,0,0.5)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
  },
  modal: {
    backgroundColor: "white",
    borderRadius: "16px",
    padding: "32px",
    width: "580px",
    maxWidth: "95vw",
    maxHeight: "90vh",
    overflowY: "auto",
  },
  modalHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "24px",
  },
  modalTitle: { fontSize: "22px", fontWeight: "600", color: "#111827" },
  closeBtn: {
    background: "none",
    border: "none",
    cursor: "pointer",
    color: "#6b7280",
    padding: "4px",
  },
  errorBox: {
    padding: "12px 16px",
    backgroundColor: "#fee2e2",
    color: "#991b1b",
    borderRadius: "8px",
    marginBottom: "16px",
    fontSize: "14px",
  },
  formGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, 1fr)",
    gap: "16px",
  },
  inputGroup: { marginBottom: "16px" },
  label: {
    display: "block",
    fontSize: "11px",
    fontWeight: "700",
    color: "#374151",
    marginBottom: "6px",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
  },
  hint: {
    fontWeight: "400",
    color: "#9ca3af",
    fontSize: "11px",
    textTransform: "none",
    letterSpacing: 0,
  },
  input: {
    width: "100%",
    padding: "10px 14px",
    border: "1.5px solid #e5e7eb",
    borderRadius: "10px",
    fontSize: "14px",
    outline: "none",
    boxSizing: "border-box",
    fontFamily: "'Poppins', sans-serif",
    color: "#1f2937",
    backgroundColor: "#fff",
  },
  textarea: {
    width: "100%",
    padding: "10px 14px",
    border: "1.5px solid #e5e7eb",
    borderRadius: "10px",
    fontSize: "14px",
    outline: "none",
    resize: "vertical",
    fontFamily: "'Poppins', sans-serif",
    boxSizing: "border-box",
    color: "#1f2937",
    backgroundColor: "#fff",
  },
  filePickerBtn: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    width: "100%",
    padding: "10px 14px",
    border: "1.5px dashed #d1d5db",
    borderRadius: "10px",
    backgroundColor: "#f9fafb",
    color: "#6b7280",
    fontSize: "14px",
    cursor: "pointer",
    boxSizing: "border-box",
    transition: "border-color 0.15s, color 0.15s",
  },
  modalFooter: { display: "flex", gap: "12px", marginTop: "8px" },
  cancelBtn: {
    flex: 1,
    padding: "12px",
    backgroundColor: "#f3f4f6",
    color: "#374151",
    border: "none",
    borderRadius: "8px",
    fontSize: "15px",
    fontWeight: "600",
    cursor: "pointer",
  },
  saveBtn: {
    flex: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    padding: "12px",
    backgroundColor: "#16a34a",
    color: "white",
    border: "none",
    borderRadius: "8px",
    fontSize: "15px",
    fontWeight: "600",
    cursor: "pointer",
  },
};

export default ManageRooms;
